import { Helmet } from 'react-helmet';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useSEO } from '@/hooks/useSEO';
import { ImageMetadata } from './SEO';

interface StructuredDataProps {
  images?: ImageMetadata[];
  pageType?: 'home' | 'menu' | 'about' | 'contact' | 'blog' | 'default';
}

const StructuredData = ({ images = [], pageType = 'default' }: StructuredDataProps) => {
  const { contactInfo, socialMedia } = useSiteSettings();
  const { seoData } = useSEO();
  
  const sameAs = [];
  if (socialMedia.tiktok) sameAs.push(socialMedia.tiktok);
  if (socialMedia.instagram) sameAs.push(socialMedia.instagram);
  if (socialMedia.facebook) sameAs.push(socialMedia.facebook);
  if (socialMedia.twitter) sameAs.push(socialMedia.twitter);

  // Determine schema type based on SEO settings
  const schemaType = seoData?.schema_type || "Restaurant";
  
  const baseSchema = {
    "@context": "https://schema.org",
    "name": "Capital District Social",
    "image": "https://capitaldistrictsocial.com/og-image.jpg",
    "description": "Modern American bar & restaurant. Craft cocktails, delicious food, and a vibrant social atmosphere.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": contactInfo.address.split(',')[0] || "",
      "addressLocality": contactInfo.address.split(',')[1]?.trim() || "",
      "addressRegion": contactInfo.address.split(',')[2]?.trim().split(' ')[0] || "",
      "postalCode": contactInfo.address.split(',')[2]?.trim().split(' ')[1] || "",
      "addressCountry": "US"
    },
    "telephone": contactInfo.phone.replace(/[^0-9+]/g, ''),
    "email": contactInfo.email,
    "sameAs": sameAs
  };

  // Restaurant-specific schema
  const restaurantSchema = {
    ...baseSchema,
    "@type": "Restaurant",
    "servesCuisine": "American",
    "priceRange": "$$",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday"],
        "opens": "11:00",
        "closes": "21:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Friday", "Saturday"],
        "opens": "11:00",
        "closes": "23:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Sunday",
        "opens": "11:00",
        "closes": "21:00"
      }
    ]
  };

  // LocalBusiness schema
  const localBusinessSchema = {
    ...baseSchema,
    "@type": "LocalBusiness"
  };

  // Menu schema
  const menuSchema = {
    "@context": "https://schema.org",
    "@type": "Menu",
    "name": "Capital District Social Menu",
    "description": "American bar & grill menu featuring craft cocktails, appetizers, entrees, and more",
    "inLanguage": "en-US"
  };

  // Determine which schema to use
  let schema;
  switch (schemaType) {
    case 'LocalBusiness':
      schema = localBusinessSchema;
      break;
    case 'Menu':
      schema = menuSchema;
      break;
    case 'Restaurant':
    default:
      schema = restaurantSchema;
      break;
  }

  // Generate ImageObject schemas for all images
  const siteUrl = 'https://capitaldistrictsocial.com';
  const imageSchemas = images.map((img, idx) => {
    const fullUrl = img.url.startsWith('http') ? img.url : `${siteUrl}${img.url}`;
    return {
      "@context": "https://schema.org",
      "@type": "ImageObject",
      "@id": `${siteUrl}#image-${idx}`,
      "name": img.alt || `Capital District Social Image ${idx + 1}`,
      "contentUrl": fullUrl,
      "url": fullUrl,
      "description": img.alt || "Capital District Social - American Bar & Restaurant",
      "author": {
        "@type": "Organization",
        "name": "Capital District Social"
      },
      ...(img.width && { "width": `${img.width}px` }),
      ...(img.height && { "height": `${img.height}px` }),
      "inLanguage": "en-US"
    };
  });

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
      
      {/* Individual image schemas */}
      {imageSchemas.map((imgSchema, idx) => (
        <script key={`img-schema-${idx}`} type="application/ld+json">
          {JSON.stringify(imgSchema)}
        </script>
      ))}
    </Helmet>
  );
};

export default StructuredData;
